import { useState, type FormEvent } from 'react';
import { BriefcaseBusiness, Pencil, Power, UserRound } from 'lucide-react';
import { toast } from 'react-toastify';
import WeeklyScheduleEditor from '../../../../common/addressSchedule/WeeklyScheduleEditor.tsx';
import { validateWeeklySchedule, type SharedScheduleDay } from '../../../../common/addressSchedule/scheduleUtils.ts';
import ConfirmModal from '../ConfirmModal.tsx';
import {
  createAgendaProfessional,
  fetchAgendaProfessionalAvailability,
  setAgendaProfessionalActive,
  updateAgendaProfessional,
  updateAgendaProfessionalAvailability,
} from '../../services/api.ts';
import type { AgendaAvailabilityPeriod, AgendaProfessional, AgendaService } from '../../types';

type ProfessionalsPanelProps = {
  professionals: AgendaProfessional[];
  services: AgendaService[];
  onChanged: () => Promise<void> | void;
};

const WEEK_DAYS = [
  { day: '0', label: 'Domingo' },
  { day: '1', label: 'Segunda-feira' },
  { day: '2', label: 'Terça-feira' },
  { day: '3', label: 'Quarta-feira' },
  { day: '4', label: 'Quinta-feira' },
  { day: '5', label: 'Sexta-feira' },
  { day: '6', label: 'Sábado' },
];

const createInitialSchedule = (): SharedScheduleDay[] => WEEK_DAYS.map((day, index) => ({
  ...day,
  closed: index === 0 || index === 6,
  periods: index === 0 || index === 6 ? [] : [{ start: '09:00', end: '18:00' }],
}));

const periodsToSchedule = (periods: AgendaAvailabilityPeriod[]): SharedScheduleDay[] => (
  WEEK_DAYS.map((day) => {
    const dayPeriods = periods
      .filter((period) => period.dayOfWeek === Number(day.day))
      .map((period) => ({ start: period.start, end: period.end }));
    return { ...day, closed: dayPeriods.length === 0, periods: dayPeriods };
  })
);

const scheduleToPeriods = (schedule: SharedScheduleDay[]): AgendaAvailabilityPeriod[] => (
  schedule.flatMap((day) => (
    day.closed
      ? []
      : day.periods.map((period) => ({
        dayOfWeek: Number(day.day),
        start: period.start,
        end: period.end,
      }))
  ))
);

const ProfessionalsPanel = ({ professionals, services, onChanged }: ProfessionalsPanelProps) => {
  const activeServices = services.filter((service) => service.active);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#0F766E');
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [schedule, setSchedule] = useState<SharedScheduleDay[]>(createInitialSchedule);
  const [scheduleError, setScheduleError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<AgendaProfessional | null>(null);

  const reset = () => {
    setEditingId(null);
    setName('');
    setDescription('');
    setColor('#0F766E');
    setServiceIds([]);
    setSchedule(createInitialSchedule());
    setScheduleError('');
  };

  const startEditing = async (professional: AgendaProfessional) => {
    setEditingId(professional.id);
    setName(professional.name);
    setDescription(professional.description || '');
    setColor(professional.color);
    setServiceIds(professional.serviceIds);
    setSchedule(createInitialSchedule());
    setScheduleError('');
    setLoadingSchedule(true);
    try {
      const periods = await fetchAgendaProfessionalAvailability(professional.id);
      setSchedule(periodsToSchedule(periods));
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Não foi possível carregar os horários.');
    } finally {
      setLoadingSchedule(false);
    }
  };

  const toggleService = (serviceId: string) => {
    setServiceIds((current) => (
      current.includes(serviceId)
        ? current.filter((id) => id !== serviceId)
        : [...current, serviceId]
    ));
  };

  const updateDay = (dayIndex: number, updater: (day: SharedScheduleDay) => SharedScheduleDay) => {
    setSchedule((current) => current.map((day, index) => (index === dayIndex ? updater(day) : day)));
    setScheduleError('');
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const defaultService = activeServices.find((service) => service.isDefault) || activeServices[0];
    const resolvedServiceIds = serviceIds.length > 0
      ? serviceIds
      : defaultService ? [defaultService.id] : [];
    if (resolvedServiceIds.length === 0) {
      toast.error('Cadastre ou ative um serviço para continuar.');
      return;
    }
    const validation = validateWeeklySchedule(schedule);
    if (validation.issues.length > 0) {
      setScheduleError(validation.issues[0].message);
      return;
    }
    setSaving(true);
    const wasCreating = !editingId;
    let savedProfessional: AgendaProfessional | null = null;
    try {
      const payload = { name, description: description || null, color, serviceIds: resolvedServiceIds };
      savedProfessional = editingId
        ? await updateAgendaProfessional(editingId, payload)
        : await createAgendaProfessional(payload);
      if (wasCreating) setEditingId(savedProfessional.id);
      const availability = await updateAgendaProfessionalAvailability(
        savedProfessional.id,
        scheduleToPeriods(schedule),
      );
      availability.warnings.forEach((warning) => toast.warning(warning));
      toast.success(editingId ? 'Profissional atualizado.' : 'Profissional cadastrado.');
      reset();
      await onChanged();
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Não foi possível salvar o profissional.';
      if (wasCreating && savedProfessional) {
        toast.error(`${message} O profissional foi criado; revise os horários e salve novamente.`);
        await Promise.resolve(onChanged()).catch(() => undefined);
      } else {
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    if (!toggleTarget) return;
    setSaving(true);
    try {
      await setAgendaProfessionalActive(toggleTarget.id, !toggleTarget.active);
      toast.success(toggleTarget.active ? 'Profissional desativado.' : 'Profissional reativado.');
      setToggleTarget(null);
      await onChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Não foi possível alterar o profissional.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="agenda-settings-layout professional-layout">
      <section className="agenda-panel">
        <div className="agenda-section-heading">
          <div>
            <span className="agenda-eyebrow"><UserRound size={15} /> Equipe</span>
            <h2>{editingId ? 'Editar profissional' : 'Novo profissional'}</h2>
            <p>O profissional é um recurso da agenda e não precisa possuir login.</p>
          </div>
        </div>
        <form className="agenda-form" onSubmit={handleSubmit}>
          <div className="agenda-form-grid">
            <label>
              <span>Nome *</span>
              <input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} required />
            </label>
            <label>
              <span>Cargo ou descrição</span>
              <input value={description} onChange={(event) => setDescription(event.target.value)} maxLength={300} />
            </label>
            <label>
              <span>Cor</span>
              <input type="color" value={color} onChange={(event) => setColor(event.target.value)} />
            </label>
          </div>

          <fieldset className="agenda-checkbox-fieldset">
            <legend>Serviços realizados (opcional)</legend>
            {activeServices.length === 0 ? (
              <p>Cadastre ou ative um serviço para continuar.</p>
            ) : (
              <p>Se nenhum for selecionado, usaremos o serviço padrão.</p>
            )}
            {services
              .filter((service) => service.active || (Boolean(editingId) && serviceIds.includes(service.id)))
              .map((service) => (
                <label key={service.id} className={service.active ? '' : 'is-disabled'}>
                  <input
                    type="checkbox"
                    checked={serviceIds.includes(service.id)}
                    disabled={!service.active}
                    onChange={() => toggleService(service.id)}
                  />
                  <span className="agenda-color-dot" style={{ backgroundColor: service.color }} />
                  {service.name}{!service.active && ' (inativo)'}
                </label>
              ))}
          </fieldset>

          <div className="agenda-schedule-editor">
            <div className="agenda-alert agenda-alert-info">
              Alterar a semana não cancela agendamentos existentes. Após salvar, revise os compromissos futuros.
            </div>
            {loadingSchedule ? <p>Carregando horários…</p> : (
              <WeeklyScheduleEditor
                days={schedule}
                error={scheduleError}
                inputClassName="agenda-time-input"
                compactTimeInputs
                closedLabel="Indisponível"
                disabled={saving}
                onToggleDayClosed={(dayIndex, closed) => updateDay(dayIndex, (day) => ({
                  ...day,
                  closed,
                  periods: closed ? [] : (day.periods.length ? day.periods : [{ start: '09:00', end: '18:00' }]),
                }))}
                onAddPeriod={(dayIndex) => updateDay(dayIndex, (day) => ({
                  ...day,
                  closed: false,
                  periods: [...day.periods, { start: '09:00', end: '18:00' }],
                }))}
                onUpdatePeriod={(dayIndex, periodIndex, field, value) => updateDay(dayIndex, (day) => ({
                  ...day,
                  periods: day.periods.map((period, index) => (
                    index === periodIndex ? { ...period, [field]: value } : period
                  )),
                }))}
                onRemovePeriod={(dayIndex, periodIndex) => updateDay(dayIndex, (day) => ({
                  ...day,
                  periods: day.periods.filter((_, index) => index !== periodIndex),
                }))}
                onReplicateAll={(period) => setSchedule((current) => current.map((day) => ({
                  ...day,
                  closed: false,
                  periods: [{ ...period }],
                })))}
              />
            )}
          </div>

          <div className="agenda-form-actions">
            {editingId && <button type="button" className="agenda-button ghost" onClick={reset}>Cancelar edição</button>}
            <button type="submit" className="agenda-button primary" disabled={saving}>
              {saving ? 'Salvando…' : editingId ? 'Salvar profissional' : 'Cadastrar profissional'}
            </button>
          </div>
        </form>
      </section>

      <section className="agenda-panel">
        <div className="agenda-section-heading">
          <div>
            <span className="agenda-eyebrow"><BriefcaseBusiness size={15} /> Atendimentos</span>
            <h2>Profissionais cadastrados</h2>
          </div>
        </div>
        {professionals.length === 0 ? (
          <div className="agenda-empty-state"><UserRound size={28} /><strong>Nenhum profissional cadastrado</strong><p>Cadastre quem realizará os atendimentos.</p></div>
        ) : (
          <div className="agenda-card-list">
            {professionals.map((professional) => (
              <article key={professional.id} className={`agenda-entity-card ${professional.active ? '' : 'is-inactive'}`}>
                <span className="agenda-color-dot" style={{ backgroundColor: professional.color }} />
                <div className="agenda-entity-main">
                  <div className="agenda-entity-title">
                    <strong>{professional.name}</strong>
                    {!professional.active && <span className="agenda-badge muted">Inativo</span>}
                  </div>
                  <p>{professional.description || 'Sem cargo informado'}</p>
                  <small>
                    {professional.serviceIds.filter((serviceId) => activeServices.some((service) => service.id === serviceId)).length} serviço(s) ativo(s)
                    {' · '}{professional.availabilityConfigured ? 'horários definidos' : 'sem horários semanais'}
                  </small>
                </div>
                <div className="agenda-entity-actions">
                  <button type="button" className="agenda-icon-button" onClick={() => startEditing(professional)} aria-label={`Editar ${professional.name}`}><Pencil size={17} /></button>
                  <button type="button" className="agenda-icon-button" onClick={() => setToggleTarget(professional)} aria-label={`${professional.active ? 'Desativar' : 'Ativar'} ${professional.name}`}><Power size={17} /></button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <ConfirmModal
        open={Boolean(toggleTarget)}
        title={toggleTarget?.active ? 'Desativar profissional?' : 'Reativar profissional?'}
        description={toggleTarget?.active
          ? 'A desativação será recusada se ainda houver agendamentos futuros ativos.'
          : 'O profissional voltará a aparecer para novos agendamentos.'}
        confirmLabel={toggleTarget?.active ? 'Desativar' : 'Reativar'}
        variant={toggleTarget?.active ? 'destructive' : 'primary'}
        saving={saving}
        onCancel={() => setToggleTarget(null)}
        onConfirm={handleToggle}
      />
    </div>
  );
};

export default ProfessionalsPanel;
